import { useCallback, useRef } from "react";
import fetchRetry from "fetch-retry";

import { useAuthHeader, webBaseUrl, coreApiV1BaseUrl, getTraceHeaders } from "@hooks/api";
import { makeHttpError } from "../utils/http-error";
import { parseRetryAfterMs } from "../utils/streamingFetch";
import { MAX_RETRIES, shouldRetry } from "./fetchApiRetry";

const fetchWithRetry = fetchRetry(global.fetch);

function retryDelay(attempt: number, _error: Error | null, response: Response | null): number {
  const backoff = Math.pow(2, attempt) * 100;
  const retryAfterMs = parseRetryAfterMs(response);
  return retryAfterMs !== null ? Math.max(retryAfterMs, backoff) : backoff;
}

async function fetchApi(url: string, options: RequestInit, authHeader: string | null): Promise<unknown> {
  const headers = new Headers(options.headers);
  if (authHeader) headers.set("Authorization", authHeader);
  for (const [key, value] of Object.entries(getTraceHeaders())) {
    headers.set(key, value);
  }
  if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept")) headers.set("Accept", "*/*");

  const response = await fetchWithRetry(url, {
    ...options,
    cache: "no-store",
    retries: MAX_RETRIES,
    retryDelay,
    retryOn: (attempt: number, error: Error | null, response: Response | null) =>
      shouldRetry(options.method, attempt, error, response),
    headers,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => "");
    throw makeHttpError(response.status, errorBody);
  }

  const text = await response.text();
  if (!text.trim()) return undefined;
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Failed to parse JSON response: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

export type FetchApi = <T = unknown>(path: string, options?: RequestInit) => Promise<T>;

export function useFetchApi(): { fetchWeb: FetchApi; fetchCore: FetchApi } {
  const authHeader = useAuthHeader();
  const authHeaderRef = useRef(authHeader);
  authHeaderRef.current = authHeader;

  const fetchWeb = useCallback(
    (path: string, options: RequestInit = {}) =>
      fetchApi(`${webBaseUrl()}${path}`, options, authHeaderRef.current),
    [],
  ) as FetchApi;

  const fetchCore = useCallback(
    (path: string, options: RequestInit = {}) =>
      fetchApi(`${coreApiV1BaseUrl()}${path}`, options, authHeaderRef.current),
    [],
  ) as FetchApi;

  return { fetchWeb, fetchCore };
}
